import type { FaunaAttachment, FaunaDefinition } from '@/fauna/FaunaDefinition';
import type { FaunaInstance } from '@/fauna/FaunaInstance';
import type { FloraDefinition } from '@/flora/FloraDefinition';
import type { FloraInstance } from '@/flora/FloraInstance';
import type { PropDefinition } from '@/site/PropCatalog';
import type { PavingMaterial, SiteObject } from '@/site/SiteObject';
import { findSurfaceLayer, type SurfaceLayerId } from '@/terrain/TerrainSurface';
import {
  compareTags,
  tagPhrase,
  type SemanticTag,
  type TagCategory,
  type TagEmphasis,
} from './SemanticTag';

/**
 * The semantics of a thing: what it already means by virtue of what it is, plus what the designer has
 * said about it by hand.
 *
 * ## Why implicit semantics are worth surfacing at all
 *
 * Almost every entity on a site already carries meaning the application knows and then throws away.
 * A flora definition ships a `tags` array — `wildlife-friendly`, `drought-tolerant`, `four-season
 * interest` — that the inspector displays and nothing else has ever read. A prop record names a
 * catalog asset whose category is `seating` or `lighting`. Paving records a material. A painted
 * texel records a ground layer with a documented character. All of it was sitting in the document,
 * none of it reached the render prompt, and the result was an enhancement pass told the exact height
 * of a shrub and nothing whatsoever about what the garden is *for*.
 *
 * This module is the one place that answer is assembled, so "what does the AI know about this thing?"
 * has a single implementation shared by the inspector (which shows it) and the capture manifest
 * (which sends it). Two derivations of that answer would eventually disagree, and the inspector would
 * become a lie about the render.
 *
 * ## Implicit never outranks manual
 *
 * A catalog tag is a reasonable default; a typed tag is a decision. So descriptors are emitted with
 * their source, manual ones sort first, and a manual tag in the same category as an implicit one is
 * assumed to be the more specific statement. The implicit tag is still listed — it is *true*, and
 * silently dropping true information because something more specific arrived is how a prompt loses
 * the fact that the tree is drought-tolerant the moment somebody names its cultivar.
 */

export type DescriptorSource = 'catalog' | 'record' | 'manual';

export interface SemanticDescriptor {
  category: TagCategory;
  label: string;
  note?: string;
  emphasis: TagEmphasis;
  source: DescriptorSource;
  /** Present only for manual descriptors — lets the UI offer edit and remove. */
  tagId?: string;
}

export interface SemanticProfile {
  /** Human name of the thing being described, for panel headings. */
  subject: string;
  descriptors: SemanticDescriptor[];
}

/** Descriptors, manual first, then loudest, then by category order. Stable and deterministic. */
function order(descriptors: SemanticDescriptor[]): SemanticDescriptor[] {
  const sourceRank: Record<DescriptorSource, number> = { manual: 0, record: 1, catalog: 2 };
  const emphasisRank: Record<TagEmphasis, number> = { critical: 0, emphasise: 1, mention: 2 };
  return [...descriptors].sort((a, b) => {
    const source = sourceRank[a.source] - sourceRank[b.source];
    if (source !== 0) return source;
    const emphasis = emphasisRank[a.emphasis] - emphasisRank[b.emphasis];
    if (emphasis !== 0) return emphasis;
    return a.label.localeCompare(b.label);
  });
}

/** Turn a stored tag into a descriptor. */
export function descriptorFromTag(tag: SemanticTag): SemanticDescriptor {
  const descriptor: SemanticDescriptor = {
    category: tag.category,
    label: tag.label,
    emphasis: tag.emphasis,
    source: 'manual',
    tagId: tag.id,
  };
  if (tag.note !== undefined) descriptor.note = tag.note;
  return descriptor;
}

function catalogDescriptor(category: TagCategory, label: string): SemanticDescriptor {
  return { category, label, emphasis: 'mention', source: 'catalog' };
}

function recordDescriptor(category: TagCategory, label: string): SemanticDescriptor {
  return { category, label, emphasis: 'mention', source: 'record' };
}

/* ----------------------------------- Flora ----------------------------------- */

/**
 * A catalog tag reads as a slug (`four-season-interest`). Which semantic category it belongs to is
 * decided by prefix rather than by an exhaustive table, because the catalog is data the application
 * does not own — a new tag shipped tomorrow must land somewhere sensible without a code change.
 */
function categoriseFloraTag(tag: string): TagCategory {
  if (/^(native|drought|deer|salt|pollution|wet|shade|clay|wind)/.test(tag)) return 'condition';
  if (/(interest|colou?r|fragran|bloom|texture|form|screen|specimen|accent)/.test(tag)) return 'style';
  if (/(hedge|screen|edible|shade-tree|windbreak|erosion|privacy|wildlife|pollinator)/.test(tag)) {
    return 'use';
  }
  return 'identity';
}

/** Prose for a catalog slug — the prompt should read as English, not as a database. */
export function humaniseSlug(slug: string): string {
  return slug.replace(/[-_]+/g, ' ').trim();
}

/**
 * What is known about one placed plant.
 *
 * The botanical identity is not repeated here — the manifest already states it far more precisely
 * than a tag could, and duplicating it would spend prompt budget saying `Acer rubrum` twice.
 */
export function floraSemantics(
  definition: FloraDefinition,
  instance: FloraInstance,
  manualTags: readonly SemanticTag[] = [],
): SemanticProfile {
  const descriptors: SemanticDescriptor[] = manualTags.slice().sort(compareTags).map(descriptorFromTag);

  for (const tag of definition.tags) {
    descriptors.push(catalogDescriptor(categoriseFloraTag(tag), humaniseSlug(tag)));
  }

  // Pruning is a record-level statement about this specimen, not about the species, and it is exactly
  // the kind of thing a render gets wrong: a hedged yew and a natural one share a definition.
  if (instance.pruningStyle !== 'natural') {
    descriptors.push(recordDescriptor('condition', `pruned ${instance.pruningStyle}`));
  }
  if (instance.health < 0.6) {
    descriptors.push(recordDescriptor('condition', 'in visible decline — thin, stressed foliage'));
  }

  return {
    subject: definition.commonNames[0] ?? definition.botanicalName,
    descriptors: order(descriptors),
  };
}

/* -------------------------------- Site objects -------------------------------- */

/**
 * What each paving material *is*, beyond a colour.
 *
 * The manifest already reports the enum; this is the sentence that stops an image model rendering
 * "gravel" as a bed of aquarium stones. Written as material description rather than as instruction,
 * because that is the register the rest of the prompt is in.
 */
const PAVING_CHARACTER: Record<PavingMaterial, string> = {
  flagstone: 'irregular cut stone slabs with planted or sanded joints',
  brick: 'clay pavers laid in a running or herringbone bond',
  concrete: 'poured or slab concrete with control joints',
  gravel: 'loose angular self-binding gravel, raked and edged',
  deck: 'timber decking boards with an even gap between them',
  asphalt: 'rolled bituminous asphalt with a soft-edged margin',
  water: 'open water — reflective, with a defined edge and no visible bottom',
};

/** What is known about one building, paved area, or prop. */
export function structureSemantics(
  object: SiteObject,
  prop: PropDefinition | undefined,
  manualTags: readonly SemanticTag[] = [],
): SemanticProfile {
  const descriptors: SemanticDescriptor[] = manualTags.slice().sort(compareTags).map(descriptorFromTag);

  if (object.kind === 'paving') {
    descriptors.push(recordDescriptor('material', PAVING_CHARACTER[object.paving] ?? object.paving));
  }

  // A prop's whole identity is its asset: nothing in the geometry says "Adirondack chair", and the
  // catalog record is the only place that fact exists. Without this a prop reaches the prompt as a
  // nameless box, which is precisely the case the manifest was built to eliminate for buildings.
  if (prop) {
    descriptors.push(catalogDescriptor('identity', prop.name.toLowerCase()));
    descriptors.push(catalogDescriptor('use', humaniseSlug(prop.category)));
    for (const tag of prop.tags) {
      descriptors.push(catalogDescriptor('material', humaniseSlug(tag)));
    }
  } else if (object.kind === 'prop') {
    // A prop whose asset is missing or still downloading. Saying so is better than describing the
    // placeholder massing as though the box were the design.
    descriptors.push(recordDescriptor('identity', 'a small garden fixture'));
  }

  return { subject: object.name, descriptors: order(descriptors) };
}

/* ----------------------------------- Fauna ----------------------------------- */

/**
 * What is known about one placed creature or figure.
 *
 * The species name is *not* repeated here — the manifest line states it, along with the pose and the
 * measured size, more precisely than a tag could. What this adds is the two things a silhouette can
 * never carry: what the creature is doing, and that it is alive.
 *
 * That second one is the whole reason this function exists. A 0.4 m massing block on a lawn, with no
 * manifest entry and a black ID-map silhouette, is read by an image model as an artefact — and the
 * render comes back with a smudge, a rock, or nothing at all where the rabbit was. Naming it as a
 * living animal in a stated pose is what turns the block into the creature it stands for.
 */
export function faunaSemantics(
  definition: FaunaDefinition,
  instance: FaunaInstance,
  manualTags: readonly SemanticTag[] = [],
): SemanticProfile {
  const descriptors: SemanticDescriptor[] = manualTags.slice().sort(compareTags).map(descriptorFromTag);

  // Stated as a record fact rather than a catalog one, and first among the implicit descriptors: a
  // model that misses this renders a garden ornament.
  descriptors.push(
    recordDescriptor(
      'identity',
      definition.class === 'person'
        ? 'a real person, not a statue or a mannequin'
        : `a live ${humaniseSlug(definition.class)}, not an ornament or a statue`,
    ),
  );

  // The pose is deliberately *not* a descriptor. The manifest line already states it — and a catalog
  // that names a robin's pose "On the ground" would otherwise put "on the ground" into the sentence
  // three times over: once in the phrase, once as the pose, once as the attachment below.
  descriptors.push(recordDescriptor('condition', ATTACHMENT_CHARACTER[instance.attachment]));

  for (const tag of definition.tags) {
    descriptors.push(catalogDescriptor(categoriseFaunaTag(tag), humaniseSlug(tag)));
  }

  return { subject: definition.name, descriptors: order(descriptors) };
}

/**
 * How a creature meets the world, in words the render can use. `attachment` is already the most
 * render-relevant fact a placed instance carries — a bird in the air and the same bird on a path are
 * two different pictures — and it is one the flat silhouette states badly at best.
 */
const ATTACHMENT_CHARACTER: Record<FaunaAttachment, string> = {
  ground: 'standing on the ground, in contact with the surface and casting a contact shadow',
  perch: 'perched off the ground, gripping what it stands on',
  water: 'on the water surface, with the disturbance and reflection that implies',
  air: 'in flight, clear of the ground and casting its shadow below',
};

/**
 * A fauna catalog tag reads as a slug, the same way a flora one does, and lands by prefix for the
 * same reason: the catalog is data this module does not own.
 */
function categoriseFaunaTag(tag: string): TagCategory {
  if (/^(native|migrat|nocturnal|seasonal|winter|summer)/.test(tag)) return 'condition';
  if (/(pollinator|scale|figure|wildlife)/.test(tag)) return 'use';
  return 'identity';
}

/* ----------------------------------- Ground ----------------------------------- */

/**
 * What is known about one painted ground material.
 *
 * Ground is the largest thing in most frames and, until now, the one the prompt said least about:
 * the beauty render shows a flat colour, the ID map paints ground black, and the manifest never
 * mentioned it. So a carefully painted gravel courtyard reached the model as "some greyish area",
 * and came back as lawn about as often as not.
 */
export function groundSemantics(
  layer: SurfaceLayerId,
  manualTags: readonly SemanticTag[] = [],
): SemanticProfile {
  const info = findSurfaceLayer(layer);
  const descriptors: SemanticDescriptor[] = manualTags.slice().sort(compareTags).map(descriptorFromTag);
  const name = info?.name ?? layer;
  // The material line comes from the layer itself, which is what lets a forged material carry its
  // own grounded description — "crushed red lava rock, matte and porous" — into a render prompt
  // instead of arriving as the bare word the user typed.
  descriptors.push(recordDescriptor('material', info?.character ?? name.toLowerCase()));
  return { subject: name, descriptors: order(descriptors) };
}

/* ---------------------------------- Prompting ---------------------------------- */

/**
 * A profile as prompt lines, grouped by category.
 *
 * Grouped rather than listed flat because the categories ask for different things — materials are
 * rendered, uses are staged, avoids are refused — and an image model reading "gravel; dining; no
 * furniture" as one undifferentiated list will treat all three as objects to draw.
 */
export function describeProfile(profile: SemanticProfile): string[] {
  if (profile.descriptors.length === 0) return [];
  const lines: string[] = [];
  for (const category of ['identity', 'use', 'material', 'style', 'condition', 'avoid'] as const) {
    const matching = profile.descriptors.filter((d) => d.category === category);
    if (matching.length === 0) continue;
    lines.push(`${CATEGORY_PROMPT_LABEL[category]}: ${matching.map(descriptorPhrase).join('; ')}`);
  }
  return lines;
}

const CATEGORY_PROMPT_LABEL: Record<TagCategory, string> = {
  identity: 'is',
  use: 'used as',
  material: 'made of',
  style: 'in the manner of',
  condition: 'condition',
  avoid: 'must not show',
};

/** One descriptor as prompt text, carrying its emphasis the same way {@link tagPhrase} does. */
export function descriptorPhrase(descriptor: SemanticDescriptor): string {
  // `tagPhrase` takes a statement rather than a whole tag, so a descriptor no longer has to be
  // dressed up as one with a blank id, a fake target and an empty date.
  return tagPhrase({
    category: descriptor.category,
    label: descriptor.label,
    ...(descriptor.note !== undefined ? { note: descriptor.note } : {}),
    emphasis: descriptor.emphasis,
  });
}

/**
 * A profile compressed to one line, for a manifest subject that already has a long description.
 *
 * Loses the category grouping deliberately: on a planting line the surrounding words already supply
 * the frame, and a second nested structure there costs more legibility than it buys precision.
 */
export function summariseProfile(profile: SemanticProfile, maxDescriptors = 4): string {
  const picked = profile.descriptors.slice(0, maxDescriptors);
  return picked.map(descriptorPhrase).join(', ');
}
